import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../../auth/store';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import {
  LoaderSymbol, LOADER_SYMBOLS, PROGRESS_BAR_STYLES,
  setLoaderSymbol, setLoaderColor, setProgressStyle, setProgressColor, useLoadingStore,
} from '../../components/loading';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';
import { useEditGate } from '../../components/confirm';
import { useFetchMs } from '../../hooks/useFetchMs';
import '../backlog/backlog.css';

/** Preset loader colours offered alongside the custom picker. */
/* token-exempt: user-selectable swatch data (the picker's palette values, not chrome styling) */
const PRESET_LOADER_COLORS = ['#e8670a', '#2563eb', '#0F6E56', '#9F1239', '#7C3AED', '#1a1a2e'];

/** Global login modes (item 1 — Login Options).  Working: standard ·
 *  twofactor · mfa.  sso-saml / ad are selectable-looking but disabled —
 *  the API rejects them with "requires IdP setup" until an IdP exists. */
type LoginMode = 'standard' | 'twofactor' | 'mfa' | 'sso-saml' | 'ad';
const LOGIN_MODES: Array<{
  value: LoginMode; label: string; desc: string; disabled?: boolean;
}> = [
  { value: 'standard',  label: 'Login',            desc: 'Username + password' },
  { value: 'twofactor', label: 'Two-Factor',       desc: 'Password + one-time code by email — forced for every user' },
  { value: 'mfa',       label: 'MFA',              desc: 'Password + authenticator app (TOTP) — un-enrolled users are taken to enrollment at sign-in' },
  { value: 'sso-saml',  label: 'SSO (SAML)',       desc: 'Requires IdP setup — coming soon', disabled: true },
  { value: 'ad',        label: 'Active Directory', desc: 'Requires IdP setup — coming soon', disabled: true },
];

/**
 * Settings → Default Settings
 *
 * Port of GoLive's "Default Settings" configuration form (global lab
 * workflow toggles).  Items 1 (Login Options), 5-9 + Skip 8/9 (Data Review
 * workflow — SDMS / SDMS Result Validation / SDMS Result Approval / Result
 * Validation / Result Approval), 13 (No Self-Review), 20-22 (Loading
 * Indicator / Progress Bar / Report Signing) are real, DB-backed, and save.
 * Items 2-4, 10-12, 14-19 are still decorative — Save / live DB binding for
 * those ships in a later step.
 *
 * Access — **service operator only** (SERVICE_EXCLUSIVE in navPermissions).
 * Nav-hidden for everyone else; re-checked at render time so a direct URL hit
 * also bounces.  Matches the gating Form Config uses.
 *
 * Layout (2026-05-31, after user feedback "don't want this extra things"):
 *   • Flat numbered rows — no section headings, no per-row hint subtext.
 *   • Yes/On → green, No/Off → red.
 *   • Scrollbar inside the card; the form fills the full card width so the
 *     scrollbar sits at the far right edge, not in a narrow centred column.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Service-only guard
// ─────────────────────────────────────────────────────────────────────────────
function ServiceOnlyBlock() {
  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Settings</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Default Settings</span>
          </div>
          <div className="dp-title-row"><FormTitle pageId="defaultsettings" /></div>
        </div>
      </div>
      <div className="bl-grid-area">
        <div className="bl-card bl-card-flex" style={{ padding: 32, alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', maxWidth: 480, color: 'var(--ai-muted)' }}>
            <i className="ti ti-shield-lock" style={{ fontSize: 36, color: 'var(--ai-muted)' }} />
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ai-ink)', margin: '12px 0 6px' }}>
              Service-only page
            </h2>
            <p style={{ fontSize: 13, lineHeight: 1.55, margin: 0 }}>
              Default Settings is reserved for the built-in <code style={{
                background: 'var(--ai-orange-tint)', border: '1px solid var(--ai-required-line)', padding: '1px 6px',
                borderRadius: 4, color: 'var(--ai-orange-hover)', fontFamily: 'inherit',
              }}>service</code> operator account.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Atoms
// ─────────────────────────────────────────────────────────────────────────────
function Choice({ value, opts }: { value: string; opts: [string, string] }) {
  const colourFor = (label: string): string => {
    const v = label.toLowerCase();
    if (v === 'yes' || v === 'on')  return 'var(--ai-green)';
    if (v === 'no'  || v === 'off') return 'var(--ai-red)';
    return 'var(--ai-ink)';
  };
  return (
    <span style={{ display: 'inline-flex', gap: 26, alignItems: 'center', minWidth: 130 }}>
      {opts.map((o) => {
        const active = value === o;
        const colour = active ? colourFor(o) : 'var(--ai-muted)';
        return (
          <label key={o} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: colour, fontWeight: active ? 700 : 400, cursor: 'default',
            fontSize: 13,
          }}>
            <input type="radio" checked={active} readOnly tabIndex={-1}
              style={{ accentColor: colour, pointerEvents: 'none' }} />
            {o}
          </label>
        );
      })}
    </span>
  );
}

function Box({ value, placeholder, width = 220 }: { value?: string; placeholder?: string; width?: number }) {
  return (
    <input
      type="text"
      value={value ?? ''}
      placeholder={placeholder}
      readOnly disabled
      style={{
        width, padding: '6px 10px', border: '1px solid var(--ai-line)', borderRadius: 5,
        background: 'var(--ai-readonly-bg)', color: 'var(--ai-ink)', fontSize: 13,
      }}
    />
  );
}

/** Interactive On/Off radio pair (Report Signing panel). */
function OnOff({ value, disabled, onPick }: { value: boolean; disabled?: boolean; onPick: (v: boolean) => void }) {
  const items: Array<[string, boolean]> = [['On', true], ['Off', false]];
  return (
    <span style={{ display: 'inline-flex', gap: 22, alignItems: 'center' }}>
      {items.map(([label, v]) => {
        const active = value === v;
        const colour = active ? (v ? 'var(--ai-green)' : 'var(--ai-red)') : 'var(--ai-muted)';
        return (
          <label key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: colour, fontWeight: active ? 700 : 400, cursor: disabled ? 'wait' : 'pointer', fontSize: 13 }}>
            <input type="radio" checked={active} disabled={disabled} onChange={() => onPick(v)} style={{ accentColor: colour }} />
            {label}
          </label>
        );
      })}
    </span>
  );
}

/** Interactive Yes/No radio pair (Data Review workflow settings, items 5-9). */
function YesNo({ value, disabled, onPick }: { value: boolean; disabled?: boolean; onPick: (v: boolean) => void }) {
  const items: Array<[string, boolean]> = [['Yes', true], ['No', false]];
  return (
    <span style={{ display: 'inline-flex', gap: 26, alignItems: 'center', minWidth: 130 }}>
      {items.map(([label, v]) => {
        const active = value === v;
        const colour = active ? (v ? 'var(--ai-green)' : 'var(--ai-red)') : 'var(--ai-muted)';
        return (
          <label key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: colour, fontWeight: active ? 700 : 400, cursor: disabled ? 'wait' : 'pointer', fontSize: 13 }}>
            <input type="radio" checked={active} disabled={disabled} onChange={() => onPick(v)} style={{ accentColor: colour }} />
            {label}
          </label>
        );
      })}
    </span>
  );
}

/** Interactive checkbox (Skip 8 / Skip 9). */
function SkipBox({ label, checked, disabled, onPick }: { label: string; checked: boolean; disabled?: boolean; onPick: (v: boolean) => void }) {
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13,
      color: checked ? 'var(--ai-ink)' : 'var(--ai-muted)', fontWeight: checked ? 600 : 400,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
    }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onPick(e.target.checked)}
        style={{ accentColor: 'var(--brand-accent, var(--ai-orange))' }} />
      {label}
    </label>
  );
}

/** Numbered row: "N. Label" on the left, controls on the right. */
function Row({ n, label, children }: { n: number; label: string; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '260px 1fr', columnGap: 24,
      alignItems: 'center', padding: '9px 4px', borderBottom: '1px solid var(--ai-line-soft)',
    }}>
      <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>
        <span style={{ color: 'var(--ai-muted)', marginRight: 4 }}>{n}.</span>{label}:
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export default function DefaultSettingsPage() {
  // Service-only guard.  Direct URL hit by a non-service user → blocked.
  const user = useAuthStore(s => s.user);
  const name = (user?.name ?? '').toLowerCase().trim();
  if (name !== 'service') return <ServiceOnlyBlock />;

  // ── View-then-Edit gate (BO UI Standard v2) — the SHARED config-panel flow ─
  // The page opens READ-ONLY; the toolbar Edit button unlocks the controls;
  // Save persists EVERYTHING in one audited batch (shared useSaveFlow); Cancel
  // reverts to the pre-edit snapshot.  No control saves on change any more —
  // "after Save only the data is saved" (user request 2026-07-07).
  const notify = useNotify();
  const gate = useEditGate();
  const ro = gate.readOnly;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const loadMs = useFetchMs(loading);

  // Loader / progress live in the shared loading store (applied app-wide). In
  // edit mode a pick updates the store for LIVE PREVIEW only; nothing is
  // persisted until Save.  Cancel reverts the store to the pre-edit snapshot.
  const symbol = useLoadingStore((s) => s.symbol);
  const color = useLoadingStore((s) => s.color);
  const progressStyle = useLoadingStore((s) => s.progressStyle);
  const progressColor = useLoadingStore((s) => s.progressColor);

  // ── Report Signing (ADR-0009) + Login Options — local editable state ───────
  const [signEnabled, setSignEnabled] = useState(false);
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [secondFactor, setSecondFactor] = useState('Mfa');
  const [loginMode, setLoginModeState] = useState<LoginMode>('standard');

  // ── Data Review workflow config (items 5-9 + Skip 8/9 + 13) — local editable
  // state, cross-field-reconciled LIVE (mirrors ALPACA
  // ResultEntryDefaultSettingsController.ObjectSpace_ObjectChanged); persisted
  // + cascaded into dbo.DefaultSetting on Save (mirrors SaveAction_Executing).
  // This is the SAME dbo.ResultEntryDefaultSettings row
  // AnalysisWriter.ApplySdmsSampleStatusProgressionAsync reads, so a Save here
  // takes effect immediately for both ALPACA and Alpaca Intelligence.
  const [sdms, setSdmsState] = useState(false);
  const [sdmsResultValidation, setSdmsResultValidationState] = useState(false);   // legacy Leve2DataReview
  const [sdmsResultApproval, setSdmsResultApprovalState] = useState(false);       // legacy Leve3DataReview
  const [resultValidation, setResultValidationState] = useState(false);
  const [resultApproval, setResultApprovalState] = useState(false);
  const [skipResultValidation, setSkipResultValidationState] = useState(false);
  const [skipResultApproval, setSkipResultApprovalState] = useState(false);
  const [noSelfReview, setNoSelfReviewState] = useState(false);

  // Snapshot of every editable value taken when Edit is pressed — powers Cancel.
  const snapshotRef = useRef<null | {
    loginMode: LoginMode; symbol: string; color: string;
    progressStyle: string; progressColor: string;
    signEnabled: boolean; approvalRequired: boolean; secondFactor: string;
    sdms: boolean; sdmsResultValidation: boolean; sdmsResultApproval: boolean;
    resultValidation: boolean; resultApproval: boolean;
    skipResultValidation: boolean; skipResultApproval: boolean; noSelfReview: boolean;
  }>(null);
  useEffect(() => {
    snapshotRef.current = gate.editing
      ? {
          loginMode, symbol, color, progressStyle, progressColor, signEnabled, approvalRequired, secondFactor,
          sdms, sdmsResultValidation, sdmsResultApproval, resultValidation, resultApproval,
          skipResultValidation, skipResultApproval, noSelfReview,
        }
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate.editing]);

  // Edit-mode pickers — LOCAL ONLY (live preview for loader/progress, plain
  // state for login/signing).  Guarded so a click while read-only is a no-op.
  const pickSymbol = (sym: (typeof LOADER_SYMBOLS)[number]) => { if (!ro) setLoaderSymbol(sym); };
  const pickColor = (c: string) => { if (!ro && c) setLoaderColor(c); };
  const pickStyle = (st: (typeof PROGRESS_BAR_STYLES)[number]) => { if (!ro) setProgressStyle(st); };
  const pickProgressColor = (c: string) => { if (!ro && c) setProgressColor(c); };
  const pickLoginMode = (m: LoginMode) => { if (!ro) setLoginModeState(m); };

  // Data Review pickers — cross-field reconciliation mirrors ALPACA's
  // ObjectSpace_ObjectChanged: SDMS=No forces SDMS Result Validation=No + both
  // Skip flags off; SDMS Result Validation=No forces SDMS Result Approval=No.
  const pickSdms = (v: boolean) => {
    if (ro) return;
    setSdmsState(v);
    if (!v) {
      setSdmsResultValidationState(false);
      setSdmsResultApprovalState(false);
      setSkipResultValidationState(false);
      setSkipResultApprovalState(false);
    }
  };
  const pickSdmsResultValidation = (v: boolean) => {
    if (ro) return;
    setSdmsResultValidationState(v);
    if (!v) setSdmsResultApprovalState(false);
  };
  const pickSdmsResultApproval = (v: boolean) => { if (!ro) setSdmsResultApprovalState(v); };
  const pickResultValidation = (v: boolean) => { if (!ro) setResultValidationState(v); };
  const pickResultApproval = (v: boolean) => { if (!ro) setResultApprovalState(v); };
  const pickSkipResultValidation = (v: boolean) => { if (!ro && sdms) setSkipResultValidationState(v); };
  const pickSkipResultApproval = (v: boolean) => { if (!ro && sdms) setSkipResultApprovalState(v); };
  const pickNoSelfReview = (v: boolean) => { if (!ro) setNoSelfReviewState(v); };

  // Animated demo — fills 0→100% on a loop so the chosen style + colour
  // (stripes scrolling, pulse, glow, the light→bright gradient) is visible.
  const [demoPct, setDemoPct] = useState(20);
  useEffect(() => {
    const id = window.setInterval(() => setDemoPct((p) => (p >= 100 ? 0 : p + 3)), 80);
    return () => window.clearInterval(id);
  }, []);

  /** Load the live config (login mode + report-signing) — reused by the
   *  toolbar Refresh.  Tracks `loading` so the shared FormTitle load-time chip
   *  and the Refresh spinner reflect it. */
  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/v1/app-config');
      const d = (res.data as { data?: {
        reportSignEnabled?: boolean; reportApprovalRequired?: boolean; reportSignSecondFactor?: string; loginMode?: LoginMode;
        dataReviewSettings?: {
          sdms?: boolean; sdmsResultValidation?: boolean; sdmsResultApproval?: boolean;
          resultValidation?: boolean; resultApproval?: boolean;
          skipResultValidation?: boolean; skipResultApproval?: boolean; noSelfReview?: boolean;
        };
      } }).data;
      setSignEnabled(d?.reportSignEnabled === true);
      setApprovalRequired(d?.reportApprovalRequired === true);
      setSecondFactor(d?.reportSignSecondFactor ?? 'Mfa');
      setLoginModeState(d?.loginMode ?? 'standard');
      const dr = d?.dataReviewSettings;
      setSdmsState(dr?.sdms === true);
      setSdmsResultValidationState(dr?.sdmsResultValidation === true);
      setSdmsResultApprovalState(dr?.sdmsResultApproval === true);
      setResultValidationState(dr?.resultValidation === true);
      setResultApprovalState(dr?.resultApproval === true);
      setSkipResultValidationState(dr?.skipResultValidation === true);
      setSkipResultApprovalState(dr?.skipResultApproval === true);
      setNoSelfReviewState(dr?.noSelfReview === true);
    } catch {
      notify.error('Could not load default settings.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  /** Persist EVERYTHING in one audited batch (the shared Save). E-signature
   *  implies Approval (the 2nd signature), so force Approval on when e-sign is. */
  const persistAll = async () => {
    const approval = signEnabled ? true : approvalRequired;
    await api.put('/api/v1/app-config/login-mode', { mode: loginMode });
    await api.put('/api/v1/app-config/loader-symbol', { symbol });
    await api.put('/api/v1/app-config/loader-color', { color });
    await api.put('/api/v1/app-config/progress-bar-style', { style: progressStyle });
    await api.put('/api/v1/app-config/progress-bar-color', { color: progressColor });
    await api.put('/api/v1/app-config/report-sign-enabled', { enabled: signEnabled });
    await api.put('/api/v1/app-config/report-approval-required', { enabled: approval });
    await api.put('/api/v1/app-config/report-sign-second-factor', { factor: secondFactor });
    await api.put('/api/v1/app-config/data-review-settings', {
      sdms, sdmsResultValidation, sdmsResultApproval, resultValidation, resultApproval,
      skipResultValidation, skipResultApproval, noSelfReview,
    });
  };

  /** Toolbar Save — the shared view-then-edit save (confirm → audited batch →
   *  "Saved successfully." → back to read-only). */
  const handleSave = () => {
    setSaving(true);
    void gate.save({
      formName: 'App Config',
      successMessage: 'Default settings saved successfully.',
      save: persistAll,
      reload: load,
      onError: (e: unknown) => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        notify.error(status === 403
          ? 'Administrator / service access required to change default settings.'
          : 'Save failed. Please try again.');
      },
    }).finally(() => setSaving(false));
  };

  /** Cancel — revert every editable value (incl. the live loader/progress
   *  preview in the shared store) back to the pre-edit snapshot. */
  const revert = () => {
    const s = snapshotRef.current; if (!s) return;
    setLoginModeState(s.loginMode);
    setLoaderSymbol(s.symbol as (typeof LOADER_SYMBOLS)[number]);
    setLoaderColor(s.color);
    setProgressStyle(s.progressStyle as (typeof PROGRESS_BAR_STYLES)[number]);
    setProgressColor(s.progressColor);
    setSignEnabled(s.signEnabled);
    setApprovalRequired(s.approvalRequired);
    setSecondFactor(s.secondFactor);
    setSdmsState(s.sdms);
    setSdmsResultValidationState(s.sdmsResultValidation);
    setSdmsResultApprovalState(s.sdmsResultApproval);
    setResultValidationState(s.resultValidation);
    setResultApprovalState(s.resultApproval);
    setSkipResultValidationState(s.skipResultValidation);
    setSkipResultApprovalState(s.skipResultApproval);
    setNoSelfReviewState(s.noSelfReview);
  };
  const handleCancel = () => gate.cancel(revert);

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Settings</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Default Settings</span>
          </div>
          {/* ONE heading only (BO UI Standard) — FormTitle (nav icon + title +
              load-time chip + config-gated Audit chip), with the Service badge
              as a title-row chip (like ModuleListView's title-row pills). */}
          <div className="dp-title-row">
            <FormTitle pageId="defaultsettings" auditFormName="App Config" loadMs={loadMs} loading={loading} />
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 10,
              fontSize: 11, fontWeight: 600, color: 'var(--ai-pill-ok-fg)',
              background: 'var(--ai-pill-ok-bg)', border: '1px solid var(--ai-pill-ok-fg)',
              padding: '3px 9px', borderRadius: 12,
            }}>
              <i className="ti ti-shield-check" style={{ fontSize: 12 }} />
              Service
            </span>
          </div>
        </div>
      </div>

      {/* ── Grid area — the toolbar sits ON the grey page (BO UI Standard v2:
          same layout as ModuleListView / Regulatory Compliance); only the card
          below is white and holds the form content. ─────────────────────── */}
      <div className="bl-grid-area">
        {/* Standard action bar (§18.6): view-then-Edit (shared EditGateActions —
            ✎ Edit → 💾 Save / ↩︎ Cancel) · Refresh.  The page is READ-ONLY until
            Edit; Save persists everything in one audited batch.  Audit is the
            config-gated chip on the FormTitle above (view-only).  This is a
            Class F config panel (BO UI Standard §4) — NO New/Edit/Delete/Export
            and no grid footer; there are no rows to export. */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <EditGateActions
                gate={gate}
                saving={saving}
                disabled={loading}
                onSave={handleSave}
                onCancel={handleCancel}
              />
            }
            onRefresh={() => void load()}
            refreshing={loading}
            refreshDisabled={gate.editing}
          />
        </div>

        <div className="bl-card bl-card-flex">
          <div style={{
            flex: 1, minHeight: 0, overflowY: 'auto',
            padding: '18px 28px 32px',
          }}>
            <div style={{ maxWidth: 980 }}>

              {/* 1 — Login Options (interactive; global login mode) */}
              <Row n={1} label="Login Options">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '2px 0' }}>
                  {LOGIN_MODES.map((o) => {
                    const active = loginMode === o.value;
                    const colour = o.disabled ? 'var(--ai-muted)' : active ? 'var(--ai-green)' : 'var(--ai-ink)';
                    return (
                      <label key={o.value} title={o.disabled ? 'Requires IdP setup — coming soon' : undefined}
                        style={{
                          display: 'inline-flex', alignItems: 'baseline', gap: 8, fontSize: 13,
                          color: colour, fontWeight: active ? 700 : 400,
                          cursor: o.disabled ? 'not-allowed' : ro ? 'default' : 'pointer',
                          opacity: o.disabled ? 0.65 : 1,
                        }}>
                        <input
                          type="radio" name="login-mode" checked={active}
                          disabled={o.disabled || ro}
                          onChange={() => pickLoginMode(o.value)}
                          style={{ accentColor: active ? 'var(--ai-green)' : 'var(--ai-muted)', transform: 'translateY(2px)' }}
                        />
                        <span>{o.label}</span>
                        <span style={{ fontSize: 11.5, color: o.disabled ? 'var(--ai-amber)' : 'var(--ai-muted)', fontWeight: 400 }}>
                          — {o.desc}
                        </span>
                      </label>
                    );
                  })}
                  {loginMode === 'mfa' && (
                    <div style={{
                      fontSize: 11.5, color: 'var(--ai-amber)', background: 'var(--ai-amber-bg)',
                      border: '1px solid var(--ai-required-line)', borderRadius: 5, padding: '5px 9px', maxWidth: 560,
                    }}>
                      <i className="ti ti-info-circle" style={{ marginRight: 5 }} />
                      MFA is Alpaca Intelligence-only — ALPACA keeps its previous login mode
                      (the legacy login type is not changed for MFA).
                    </div>
                  )}
                </div>
              </Row>
              <Row n={2} label="Subout Result Validation"><Choice value="Yes" opts={['Yes', 'No']} /></Row>
              <Row n={3} label="Subout Result Approval"><Choice value="No" opts={['Yes', 'No']} /></Row>
              <Row n={4} label="Result Entry Express"><Box placeholder="Analysis" /></Row>

              {/* 5-9 + Skip 8/9 — Data Review workflow config (interactive; ALPACA
                  parity — see IAppConfigReader.SetDataReviewSettingsAsync). Same
                  dbo.ResultEntryDefaultSettings row the SDMS batch
                  validate/approve status-progression logic
                  (AnalysisWriter.ApplySdmsSampleStatusProgressionAsync) reads —
                  a Save here takes effect for both ALPACA and Alpaca
                  Intelligence immediately. */}
              <Row n={5} label="SDMS">
                <YesNo value={sdms} disabled={ro} onPick={pickSdms} />
                <SkipBox label="Skip 8" checked={skipResultValidation} disabled={ro || !sdms} onPick={pickSkipResultValidation} />
                <SkipBox label="Skip 9" checked={skipResultApproval} disabled={ro || !sdms} onPick={pickSkipResultApproval} />
              </Row>

              <Row n={6} label="SDMS Result Validation">
                <YesNo value={sdmsResultValidation} disabled={ro || !sdms} onPick={pickSdmsResultValidation} />
              </Row>
              <Row n={7} label="SDMS Result Approval">
                <YesNo value={sdmsResultApproval} disabled={ro || !sdms || !sdmsResultValidation} onPick={pickSdmsResultApproval} />
              </Row>
              <Row n={8} label="Result Validation">
                <YesNo value={resultValidation} disabled={ro} onPick={pickResultValidation} />
              </Row>
              <Row n={9} label="Result Approval">
                <YesNo value={resultApproval} disabled={ro} onPick={pickResultApproval} />
              </Row>

              <Row n={10} label="Electronic Signature"><Choice value="On" opts={['On', 'Off']} /></Row>
              <Row n={11} label="Quote Validation"><Choice value="No" opts={['Yes', 'No']} /><Box placeholder="Quote Review" /></Row>
              <Row n={12} label="Data Package"><Choice value="Yes" opts={['Yes', 'No']} /></Row>
              <Row n={13} label="No Self-Review">
                <OnOff value={noSelfReview} disabled={ro} onPick={pickNoSelfReview} />
              </Row>
              <Row n={14} label="Field Data Validation"><Choice value="Yes" opts={['Yes', 'No']} /></Row>
              <Row n={15} label="Field Data Approval"><Choice value="No" opts={['Yes', 'No']} /></Row>
              <Row n={16} label="Manual Reagent Entry SDMS"><Choice value="No" opts={['Yes', 'No']} /></Row>
              <Row n={17} label="Sample Receipt Notification"><Choice value="Yes" opts={['Yes', 'No']} /></Row>
              <Row n={18} label="Subout Tracking"><Choice value="Yes" opts={['Yes', 'No']} /></Row>
              <Row n={19} label="Reporting"><Choice value="Yes" opts={['Yes', 'No']} /></Row>

              {/* 20 — Global loading indicator (interactive; client-configurable, §18.6) */}
              <div style={{ marginTop: 22, paddingTop: 18, borderTop: '2px solid var(--ai-line-soft)' }}>
                <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>
                  <span style={{ color: 'var(--ai-muted)', marginRight: 4 }}>20.</span>Loading Indicator:
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ai-muted)', margin: '4px 0 12px' }}>
                  The animation shown for every loading state app-wide — data retrieve, save, validate,
                  export, report. Applies to all users immediately.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))', gap: 10, maxWidth: 560 }}>
                  {LOADER_SYMBOLS.map((key) => {
                    const active = symbol === key;
                    return (
                      <button
                        key={key} type="button" onClick={() => pickSymbol(key)} disabled={ro}
                        aria-pressed={active}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                          padding: '16px 8px 10px', cursor: ro ? 'default' : 'pointer',
                          background: active ? 'var(--ai-orange-tint)' : '#fff',
                          border: active ? '2px solid var(--brand-accent, var(--ai-orange))' : '1px solid var(--ai-line)', borderRadius: 6,
                        }}
                      >
                        <span style={{ height: 30, display: 'inline-flex', alignItems: 'center' }}>
                          <LoaderSymbol symbol={key} size={26} />
                        </span>
                        <span style={{ fontSize: 12, fontFamily: 'var(--ai-font-mono)', color: active ? 'var(--ai-orange-hover)' : 'var(--ai-ink)' }}>{key}</span>
                      </button>
                    );
                  })}
                </div>

                <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ai-ink)' }}>Colour:</span>
                  <div style={{ display: 'inline-flex', gap: 8 }}>
                    {PRESET_LOADER_COLORS.map((c) => {
                      const active = c.toLowerCase() === color.toLowerCase();
                      return (
                        <button
                          key={c} type="button" onClick={() => pickColor(c)} disabled={ro}
                          aria-label={`Set loader colour ${c}`} title={c}
                          style={{
                            width: 26, height: 26, borderRadius: '50%', background: c,
                            cursor: ro ? 'default' : 'pointer',
                            border: active ? '2px solid var(--ai-ink)' : '1px solid var(--ai-line)',
                            boxShadow: active ? '0 0 0 2px #fff inset' : 'none',
                          }}
                        />
                      );
                    })}
                  </div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ai-muted)' }}>
                    Custom
                    <input
                      type="color"
                      /* token-exempt: <input type=color> value must be a literal hex */
                      value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : '#e8670a'}
                      onChange={(e) => pickColor(e.target.value)} disabled={ro}
                      style={{ width: 34, height: 26, padding: 0, border: '1px solid var(--ai-line)', borderRadius: 4, cursor: 'pointer' }}
                    />
                  </label>
                  <span style={{ fontFamily: 'var(--ai-font-mono)', fontSize: 12, color: 'var(--ai-muted)' }}>{color}</span>
                </div>

                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ai-muted)' }}>
                  {/* token-exempt: literal default value shown as help text */}
                  Default symbol <code>ring</code>, colour <code>#e8670a</code> — both apply to all users immediately.
                </div>
              </div>

              {/* 21 — Progress bar (interactive; client-configurable, §18.6) */}
              <div style={{ marginTop: 22, paddingTop: 18, borderTop: '2px solid var(--ai-line-soft)' }}>
                <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>
                  <span style={{ color: 'var(--ai-muted)', marginRight: 4 }}>21.</span>Progress Bar:
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ai-muted)', margin: '4px 0 12px' }}>
                  Shown for determinate progress (exports, report generation) and the top-of-page
                  loading bar — everywhere a progress bar appears. Applies to all users immediately.
                </div>

                {/* Live demo bar — fills 0→100% on a loop so you can SEE the
                    current style (stripes / pulse / glow) + the light→bright colour. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ height: 10, width: 300, maxWidth: '100%', background: 'var(--ai-loader-track)', borderRadius: 5, overflow: 'hidden' }}>
                    <div className="ld-pbar-fill" style={{ height: '100%', width: `${demoPct}%`, borderRadius: 5, transition: 'width .08s linear' }} />
                  </div>
                  <span style={{ fontFamily: 'var(--ai-font-mono)', fontSize: 12, color: 'var(--ai-muted)', minWidth: 36 }}>{demoPct}%</span>
                </div>

                {/* Style options */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ai-ink)' }}>Style:</span>
                  {PROGRESS_BAR_STYLES.map((st) => {
                    const active = progressStyle === st;
                    return (
                      <button
                        key={st} type="button" onClick={() => pickStyle(st)} disabled={ro}
                        aria-pressed={active}
                        style={{
                          padding: '6px 14px', fontSize: 12.5, fontFamily: 'monospace', borderRadius: 6,
                          cursor: ro ? 'default' : 'pointer',
                          background: active ? 'var(--ai-orange-tint)' : '#fff',
                          border: active ? '2px solid var(--brand-accent, var(--ai-orange))' : '1px solid var(--ai-line)',
                          color: active ? 'var(--ai-orange-hover)' : 'var(--ai-ink)',
                        }}
                      >{st}</button>
                    );
                  })}
                </div>

                {/* Colour */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ai-ink)' }}>Colour:</span>
                  <div style={{ display: 'inline-flex', gap: 8 }}>
                    {PRESET_LOADER_COLORS.map((c) => {
                      const active = c.toLowerCase() === progressColor.toLowerCase();
                      return (
                        <button
                          key={c} type="button" onClick={() => pickProgressColor(c)} disabled={ro}
                          aria-label={`Set progress-bar colour ${c}`} title={c}
                          style={{
                            width: 26, height: 26, borderRadius: '50%', background: c,
                            cursor: ro ? 'default' : 'pointer',
                            border: active ? '2px solid var(--ai-ink)' : '1px solid var(--ai-line)',
                            boxShadow: active ? '0 0 0 2px #fff inset' : 'none',
                          }}
                        />
                      );
                    })}
                  </div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ai-muted)' }}>
                    Custom
                    <input
                      type="color"
                      /* token-exempt: <input type=color> value must be a literal hex */
                      value={/^#[0-9a-fA-F]{6}$/.test(progressColor) ? progressColor : '#e8670a'}
                      onChange={(e) => pickProgressColor(e.target.value)} disabled={ro}
                      style={{ width: 34, height: 26, padding: 0, border: '1px solid var(--ai-line)', borderRadius: 4, cursor: 'pointer' }}
                    />
                  </label>
                  <span style={{ fontFamily: 'var(--ai-font-mono)', fontSize: 12, color: 'var(--ai-muted)' }}>{progressColor}</span>
                </div>

                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ai-muted)' }}>
                  {/* token-exempt: literal default value shown as help text */}
                  Default style <code>solid</code>, colour <code>#e8670a</code> — applies to every progress bar in the app.
                </div>
              </div>

              {/* 22 — Report Signing (interactive; ADR-0009) */}
              <div style={{ marginTop: 22, paddingTop: 18, borderTop: '2px solid var(--ai-line-soft)' }}>
                <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>
                  <span style={{ color: 'var(--ai-muted)', marginRight: 4 }}>22.</span>Report Signing (e-signature):
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ai-muted)', margin: '4px 0 14px' }}>
                  When enabled, Report Validation / Approval require an electronic signature —
                  password + a second factor + a certification. Applies to all users immediately.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', columnGap: 24, rowGap: 14, alignItems: 'center', maxWidth: 760 }}>
                  <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>Require e-signature:</div>
                  <OnOff value={signEnabled} disabled={ro}
                    onPick={(v) => {
                      setSignEnabled(v);
                      // E-signature implies a two-signature workflow: turning it on forces
                      // Approval (the 2nd signature) on; it cannot be turned off while e-sign is on.
                      if (v) setApprovalRequired(true);
                    }} />

                  <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>Require Approval (2nd signature):</div>
                  <OnOff value={signEnabled || approvalRequired} disabled={ro || signEnabled}
                    onPick={(v) => setApprovalRequired(v)} />

                  <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>Second factor (besides password):</div>
                  <select value={secondFactor} disabled={ro}
                    onChange={(e) => setSecondFactor(e.target.value)}
                    style={{ width: 240, padding: '6px 10px', border: '1px solid var(--ai-line)', borderRadius: 5, background: '#fff', color: 'var(--ai-ink)', fontSize: 13 }}>
                    <option value="Mfa">Authenticator app (MFA)</option>
                    <option value="Email">One-time code by Email</option>
                    <option value="Sms">One-time code by SMS</option>
                  </select>
                </div>
                <div style={{ marginTop: 12, fontSize: 11, color: 'var(--ai-muted)' }}>
                  Password is always required. {signEnabled
                    ? <>While e-signature is required, <b>Approval is mandatory</b> (two signatures) and cannot be turned off.</>
                    : <>Approval defaults <code>Off</code> (one signature at validation).</>}
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
